import type * as Referrals from "../../lib/models/referrals.server.ts"
import type * as ShopifyProduct from "../../lib/models/shopify-product.server.ts"
import type * as Team from "../../lib/models/team.server.ts"

import type { ProductVariantFragment } from "../../lib/shopify/types/storefront.generated"

import * as React from "react"
import * as Shopify from "../../lib/shopify/shopify.ts"
import * as Dialog from "../../lib/ui/dialog.tsx"

import { when } from "@iyk/flow"
import { Link } from "@remix-run/react"
import { TeamHeadingLink } from "../profiles/team-heading-link.tsx"
import { ReferralAlert } from "../referrals/referral-alert.tsx"
import { Shop } from "./shop.tsx"

export function ShopDialog({
  team,
  shopifyProduct,
  referral,
  withDescription,
  openShopDialog: controlledIsOpen,
  setOpenShopDialog: controlledSetIsOpen,
  trigger,
}: ShopDialogProps) {
  const [isOpen, setIsOpen] = useControlledState(controlledIsOpen, controlledSetIsOpen, false)

  const { productId, shopifyProductData } = shopifyProduct ?? {}

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      {trigger ? (
        <Dialog.Trigger asChild>
          {trigger({
            defaultVariant: shopifyProductData
              ? Shopify.getDefaultVariant(shopifyProductData.variants.nodes)
              : undefined,
          })}
        </Dialog.Trigger>
      ) : null}
      <Dialog.Content size="xl" className="w-full pt-9 px-0">
        <div className="sr-only">
          <Dialog.Title>Shop product</Dialog.Title>
          <Dialog.Description>View product details and add to cart</Dialog.Description>
        </div>
        {shopifyProductData && (
          <Shop.Root shopifyProduct={shopifyProductData} referral={referral}>
            <div className="grid grid-cols-1 md:grid-cols-2 grid-rows-[minmax(auto,1fr),auto] max-h-[calc(100%-40px)]">
              <div className="col-span-1 md:col-span-2 row-span-1 p-4 mb-4 max-h-[calc(100vh-180px)] overflow-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Shop.Media className="w-full" />
                  <div className="flex flex-col justify-center gap-3">
                    {when(referral, (referral) => (
                      <ReferralAlert referral={referral} className="mb-1" />
                    ))}
                    <div className="flex flex-col items-start gap-2">
                      <TeamHeadingLink {...team} />
                      <Link to={`/creators/${team.urlSlug}/items/${productId}`}>
                        <span className="text-xl">{shopifyProductData.title}</span>
                      </Link>
                    </div>
                    <Shop.Container>
                      <Shop.Price />
                      <Shop.Variants />
                      {withDescription && shopifyProductData.description && (
                        <span className="text-sm">{shopifyProductData.description}</span>
                      )}
                    </Shop.Container>
                  </div>
                </div>
              </div>
              <div className="col-span-1 md:col-span-2 px-4">
                <Shop.AddToCartButton onAddToCartComplete={() => setIsOpen(false)} />
              </div>
            </div>
          </Shop.Root>
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}

function useControlledState<T>(
  controlledValue: T | undefined,
  setControlledValue: ((value: T) => void) | undefined,
  defaultValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [internalValue, setInternalValue] = React.useState(defaultValue)
  const value = controlledValue ?? internalValue
  const setValue = (value: T | ((prev: T) => T)) => {
    if (setControlledValue) {
      setControlledValue(
        typeof value === "function" ? (value as (prev: T) => T)(controlledValue!) : value,
      )
    } else {
      setInternalValue(value)
    }
  }
  return [value, setValue]
}

type ShopDialogProps = {
  team: Team.ProfilePreview
  shopifyProduct: ShopifyProduct.ShopifyProductWithData | null
  referral?: Referrals.ReferralData | null
  withDescription?: boolean
  openShopDialog?: boolean
  setOpenShopDialog?: (open: boolean) => void
  trigger?: (args: { defaultVariant?: ProductVariantFragment }) => React.ReactNode
}
